import { motion } from "framer-motion";
import { Home, Waves, Sun, Shell } from "lucide-react";
import { WHATSAPP_URL } from "../App";

const t = {
  es: {
    eyebrow: "Monterrico, Guatemala",
    headline1: "Lujo Costero",
    headline2: "a Tu Alcance",
    subtitle:
      "Vive la serenidad de la costa del Pacífico en nuestros bungalows diseñados para tu comodidad, descanso y exclusividad.",
    cta: "Reservar por WhatsApp",
    scroll: "Conoce más",
    features: [
      { icon: Home, label: "Bungalows Familiares" },
      { icon: Waves, label: "Piscinas" },
      { icon: Sun, label: "Sol y Playa" },
      { icon: Shell, label: "Acceso al Mar" },
    ],
  },
  en: {
    eyebrow: "Monterrico, Guatemala",
    headline1: "Coastal Luxury",
    headline2: "Within Reach",
    subtitle:
      "Experience the serenity of the Pacific coast in our thoughtfully designed bungalows, curated for comfort and exclusivity.",
    cta: "Book via WhatsApp",
    scroll: "Learn more",
    features: [
      { icon: Home, label: "Family Bungalows" },
      { icon: Waves, label: "Swimming Pools" },
      { icon: Sun, label: "Sun & Beach" },
      { icon: Shell, label: "Ocean Access" },
    ],
  },
};

export default function Hero({ lang }) {
  const tr = t[lang];

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden"
    >
      {/* Background image - real pool/beach photo */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('/images/galeria/P1.jpg')`,
        }}
      />
      {/* Gradient overlay - stronger at top for navbar contrast */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#1F3A5E]/85 via-[#1F3A5E]/55 to-[#1F3A5E]/75" />

      {/* Content */}
      <div className="relative z-10 text-center px-6 max-w-4xl mx-auto pt-24">
        {/* Eyebrow */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="font-montserrat text-[#D4B895] text-sm font-semibold tracking-widest uppercase mb-6"
          data-testid="hero-eyebrow"
        >
          {tr.eyebrow}
        </motion.p>

        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="font-montserrat font-extrabold text-white leading-tight mb-4"
          style={{ fontSize: "clamp(2.8rem, 7vw, 5.5rem)" }}
          data-testid="hero-headline"
        >
          {tr.headline1}
          <br />
          <span className="text-[#D4B895]">{tr.headline2}</span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="font-dm-sans text-white/80 text-base md:text-lg max-w-2xl mx-auto mb-10 leading-relaxed"
          data-testid="hero-subtitle"
        >
          {tr.subtitle}
        </motion.p>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.45 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-btn"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#25D366] text-white rounded-full font-montserrat font-bold text-base hover:-translate-y-1 hover:shadow-xl hover:shadow-[#25D366]/30 transition-transform duration-200"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            {tr.cta}
          </a>
          <a
            href="#about"
            data-testid="hero-scroll-btn"
            className="font-montserrat text-white/80 text-sm font-medium hover:text-white transition-colors duration-200 flex items-center gap-2 group"
          >
            {tr.scroll}
            <span className="w-5 h-5 rounded-full border border-white/40 flex items-center justify-center group-hover:border-white transition-colors duration-200">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </span>
          </a>
        </motion.div>
      </div>

      {/* Feature Pills */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.6 }}
        className="relative z-10 mt-16 px-6 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-3xl mx-auto w-full"
      >
        {tr.features.map(({ icon: Icon, label }, i) => (
          <div
            key={i}
            data-testid={`hero-feature-${i}`}
            className="flex flex-col items-center gap-2 bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/20"
          >
            <Icon size={22} className="text-[#D4B895]" />
            <span className="font-dm-sans text-white text-xs text-center font-medium">{label}</span>
          </div>
        ))}
      </motion.div>
    </section>
  );
}
